// LayerContext.tsx
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Map } from 'ol';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { AddLayer, AddVectorTile, LayerItem } from './types';
import { areLabelsActive } from '../styles/areLabelsActive';
import { getFeatureStyle, StyleFunction } from '../styles/getStyle';
import { HoverState, useVectorTiles } from '../vectorTiles/useVectorTiles';
import { useMapLayerHandler } from './useMapLayerHandler';
import { useMaxZoomOnLayers } from './useMaxZoomOnLayers';
import { getLayerTypeWithShips } from '../getLayerType';
import { baseColours } from '../styles/baseColours';
import { useTimeZoneContext } from '@ais-ui/utils';
import Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import {
  addNewLayerAboveShipsLayer,
  addNewLayerAtTop,
  addNewLayerBelowShipsLayer,
  updateZIndex,
} from './addLayers';
import { calculateExtentWithAntimeridianSupport } from '../utils/calculateExtentWithAntimeridianSupport';
import { useOpenLayersContext } from '../OpenLayersContext';

export type RemoveLayerOnThresholdExceeded = (
  layer: LayerItem,
  onLoadFail: (layer: LayerItem) => void | undefined
) => void;

interface LayerContextProps {
  layers: LayerItem[];
  addLayer: AddLayer;
  addLayerAtIndex: (
    layer: BaseLayer,
    name: string,
    index: number,
    isRemovableFromLayerControl?: boolean,
    canZoomToFromLayerControl?: boolean,
    canShowLabel?: boolean,
    properties?: Record<string, any>,
    id?: string,
    showLabel?: boolean,
    clickable?: boolean,
    hoverEnabled?: boolean
  ) => LayerItem;
  addExistingLayerItem: (
    layer: BaseLayer,
    layerItem: LayerItem,
    position?: 'above-ships' | 'below-ships' | 'top'
  ) => void;
  addBaseLayer: (layer: BaseLayer, name: string, id?: string) => void;
  removeLayer: (layer: BaseLayer) => void;
  rearrangeLayer: (layer: LayerItem, newIndex: number) => void;
  getLayerById: (id: string) => LayerItem | undefined;
  doesLayerExist: (id: string) => boolean;
  zoomToLayer: (layer: VectorLayer<Feature<OlGeometry>>) => void;
  zoomToExtent: (extent: number[]) => void;
  toggleLayerVisibility: (layer: BaseLayer, isVisible: boolean) => void;
  removeLayerById: (id: string | undefined) => void;
  getBaseLayer: () => LayerItem | undefined;
  removeLayerOnThresholdExceeded: RemoveLayerOnThresholdExceeded;
  toggleLabels: (layer: LayerItem) => void;
  addVectorTile: AddVectorTile;
  removeLabels: (layer: LayerItem) => void;
  addLabels: (layer: LayerItem) => void;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  isCanvasLoaded: boolean;
  setIsCanvasLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  vectorTileHoverState: HoverState;
  changeColor: (layer: LayerItem, color: keyof typeof baseColours) => void;
  changeTransparency: (layer: LayerItem, transparency: number) => void;
  getLayersByIdContaining: (idSubstring: string) => LayerItem[];
  isShipLayerVisible: boolean;
}

const LayerContext = createContext<LayerContextProps | undefined>(undefined);

interface LayerProviderProps {
  map: Map;
  isDrawing: boolean;
  children: ReactNode;
}

export const initLayerItem = (
  layer: BaseLayer,
  name: string,
  isBase = false,
  isRemovableFromLayerControl = false,
  canZoomToFromLayerControl = false,
  canShowLabel = false,
  properties?: Record<string, any>,
  id?: string
): LayerItem => {
  const usedId = id || uuidv4();

  layer.set('id', usedId);
  layer.set('name', name);
  layer.set('isBase', isBase);
  layer.set('isRemovableFromLayerControl', isRemovableFromLayerControl);
  layer.set('canZoomToFromLayerControl', canZoomToFromLayerControl);
  layer.set('canShowLabel', canShowLabel);
  layer.set('properties', properties ? properties : {});

  const errorCount = 0;
  const successCount = 0;

  const layerItemWithoutType: LayerItem = {
    id: usedId,
    name,
    isBase,
    isVisible: true,
    layer,
    isRemovableFromLayerControl,
    canZoomToFromLayerControl,
    canShowLabel,
    properties: properties ? properties : {},
    errorCount,
    successCount,
    type: 'unknown',
  };

  const layerType = getLayerTypeWithShips(layerItemWithoutType);

  return {
    ...layerItemWithoutType,
    type: layerType,
  };
};

const isLayerThresholdExceeded = (layer: LayerItem) => {
  const errorThreshold = 10;
  const successRatio =
    layer.successCount / (layer.successCount + layer.errorCount);

  return layer.errorCount > errorThreshold && successRatio < 0.2;
};

function updateLayerPropertiesAndStyle(
  layer: LayerItem,
  setLayers: React.Dispatch<React.SetStateAction<LayerItem[]>>,
  vectorLayer: VectorLayer<Feature<OlGeometry>>,
  showLabel: boolean,
  color: keyof typeof baseColours = 'blue',
  transparency: number,
  timezone?: string
) {
  // Update the layer properties to trigger re-renders
  setLayers((prevLayers) =>
    prevLayers.map((l) =>
      l.id === layer.id
        ? {
            ...l,
            properties: {
              ...l.properties,
              showLabel: showLabel,
              color: color,
              transparency: transparency,
            },
          }
        : l
    )
  );

  if (
    layer.properties.type !== 'port' &&
    layer.properties.type !== 'source-identifier' &&
    layer.properties.type !== 'coverage' &&
    layer.properties.type !== 'gps-lost'
  ) {
    vectorLayer.setStyle((feature) => {
      return getFeatureStyle(
        feature,
        color,
        'medium',
        'medium',
        showLabel,
        transparency,
        timezone
      );
    });
  }
}

export const LayerProvider = ({
  map,
  isDrawing,
  children,
}: LayerProviderProps) => {
  const [layers, setLayers] = useState<LayerItem[]>([]);
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { timeZone } = useTimeZoneContext();

  const vectorTileLayers = useMemo(
    () =>
      layers.filter((layer) => {
        return layer.type === 'vectortile';
      }),
    [layers]
  );

  // canvasRef is used to render ship image
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [isCanvasLoaded, setIsCanvasLoaded] = useState(false);

  const toggleLabels = useCallback(
    (layer: LayerItem) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const styleFunction = vectorLayer.getStyleFunction();
      const labelsAreActive = areLabelsActive(
        styleFunction as StyleFunction,
        source
      );

      const color = layer.properties?.color || 'blue';
      const transparency = layer.properties?.transparency || 0;

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        !labelsAreActive,
        color,
        transparency,
        timeZone
      );
    },
    [timeZone]
  );

  const removeLabels = useCallback(
    (layer: LayerItem) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const color = layer.properties.color || 'blue';
      const transparency = layer.properties?.transparency || 0;

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        false,
        color,
        transparency,
        timeZone
      );
    },
    [timeZone]
  );

  const addLabels = useCallback(
    (layer: LayerItem) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const color = layer.properties?.color || 'blue';
      const transparency = layer.properties?.transparency || 0;

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        true,
        color,
        transparency,
        timeZone
      );
    },
    [timeZone]
  );

  const changeColor = useCallback(
    (layer: LayerItem, color: keyof typeof baseColours) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const showLabel = layer.properties?.showLabel || false;
      const transparency = layer.properties?.transparency || 0;

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        showLabel,
        color,
        transparency,
        timeZone
      );
    },
    [timeZone]
  );

  const changeTransparency = useCallback(
    (layer: LayerItem, transparency: number) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const showLabel = layer.properties.showLabel || false;
      const color = layer.properties.color || 'blue';

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        showLabel,
        color,
        transparency,
        timeZone
      );
    },
    [timeZone]
  );

  // Internal helper function for common layer creation logic
  const createLayerItem = useCallback(
    (
      layer: BaseLayer,
      name: string,
      isRemovableFromLayerControl = false,
      canZoomToFromLayerControl = false,
      canShowLabel = false,
      properties?: Record<string, any>,
      id?: string,
      showLabel?: boolean,
      clickable?: boolean,
      hoverEnabled?: boolean,
      contextMenuEnabled?: boolean
    ): LayerItem => {
      // We cannot use properties for showLabel, as only the owner of the shape can change these. So instead
      //  we use the showLabel parameter to populate the properties object, when the shape is added to the map.
      if (properties) {
        properties.showLabel = !!showLabel;
        properties.clickable = !!clickable;
        properties.hoverEnabled = !!hoverEnabled;
        properties.contextMenuEnabled = !!contextMenuEnabled;
      }

      const newLayerItem = initLayerItem(
        layer,
        name,
        false,
        isRemovableFromLayerControl,
        canZoomToFromLayerControl,
        canShowLabel,
        properties,
        id
      );

      map.addLayer(layer);

      if (canShowLabel && showLabel) {
        addLabels(newLayerItem);
      }

      return newLayerItem;
    },
    [addLabels, map]
  );

  // Internal helper function for positioning layer based on string position
  const addLayerWithStringPosition = useCallback(
    (
      newLayerItem: LayerItem,
      position: 'above-ships' | 'below-ships' | 'top' = 'below-ships'
    ) => {
      setLayers((prevLayers) => {
        if (position === 'above-ships') {
          return addNewLayerAboveShipsLayer(prevLayers, newLayerItem);
        } else if (position === 'top') {
          return addNewLayerAtTop(prevLayers, newLayerItem);
        } else {
          return addNewLayerBelowShipsLayer(prevLayers, newLayerItem);
        }
      });
    },
    []
  );

  // Internal helper function for positioning layer at specific index
  const addLayerWithNumericPosition = useCallback(
    (newLayerItem: LayerItem, index: number) => {
      setLayers((prevLayers) => {
        const updatedLayers = [...prevLayers];
        // Insert the layer at the specific index
        updatedLayers.splice(index, 0, newLayerItem);

        // Update Z-Index
        updateZIndex(updatedLayers);

        return updatedLayers;
      });
    },
    []
  );

  const addLayer = useCallback(
    (
      layer: BaseLayer,
      name: string,
      isRemovableFromLayerControl = false,
      canZoomToFromLayerControl = false,
      canShowLabel = false,
      properties?: Record<string, any>,
      id?: string,
      showLabel?: boolean,
      clickable?: boolean,
      hoverEnabled?: boolean,
      position: 'above-ships' | 'below-ships' | 'top' = 'below-ships',
      contextMenuEnabled?: boolean
    ): LayerItem => {
      const newLayerItem = createLayerItem(
        layer,
        name,
        isRemovableFromLayerControl,
        canZoomToFromLayerControl,
        canShowLabel,
        properties,
        id,
        showLabel,
        clickable,
        hoverEnabled,
        contextMenuEnabled
      );

      addLayerWithStringPosition(newLayerItem, position);

      return newLayerItem;
    },
    [createLayerItem, addLayerWithStringPosition]
  );

  const addLayerAtIndex = useCallback(
    (
      layer: BaseLayer,
      name: string,
      index: number,
      isRemovableFromLayerControl = false,
      canZoomToFromLayerControl = false,
      canShowLabel = false,
      properties?: Record<string, any>,
      id?: string,
      showLabel?: boolean,
      clickable?: boolean,
      hoverEnabled?: boolean,
      contextMenuEnabled?: boolean
    ): LayerItem => {
      const newLayerItem = createLayerItem(
        layer,
        name,
        isRemovableFromLayerControl,
        canZoomToFromLayerControl,
        canShowLabel,
        properties,
        id,
        showLabel,
        clickable,
        hoverEnabled,
        contextMenuEnabled
      );

      addLayerWithNumericPosition(newLayerItem, index);

      return newLayerItem;
    },
    [createLayerItem, addLayerWithNumericPosition]
  );

  useMaxZoomOnLayers(map, layers);

  const { addVectorTile, vectorTileHoverState, onVectorTileHover } =
    useVectorTiles(addLayer);

  // This function is created so that we can start with a layerItem that has an empty layer, and thus track
  //  tile loading successes and errors from the start. If possible, use addLayer instead.
  const addExistingLayerItem = useCallback(
    (
      layer: BaseLayer,
      layerItem: LayerItem,
      position: 'above-ships' | 'below-ships' | 'top' = 'below-ships'
    ) => {
      layerItem.layer = layer;
      setLayers((prevLayers) => {
        if (position === 'above-ships') {
          return addNewLayerAboveShipsLayer(prevLayers, layerItem);
        } else if (position === 'top') {
          return addNewLayerAtTop(prevLayers, layerItem);
        } else {
          return addNewLayerBelowShipsLayer(prevLayers, layerItem);
        }
      });

      map.addLayer(layer);
    },
    [map]
  );

  const removeLayer = useCallback(
    (layer: BaseLayer) => {
      map.removeLayer(layer);
      setLayers((prevLayers) => prevLayers.filter((l) => l.layer !== layer));
    },
    [map]
  );

  const addBaseLayer = useCallback(
    (layer: BaseLayer, name: string, id?: string) => {
      const newLayerItem = initLayerItem(
        layer,
        name,
        true,
        false,
        false,
        false,
        undefined,
        id
      );

      setLayers((prevLayers) => {
        const baseLayers = prevLayers.filter((l) => l.isBase);
        baseLayers.forEach((baseLayerItem) => {
          removeLayer(baseLayerItem.layer);
        });

        const updatedLayers = [
          newLayerItem,
          ...prevLayers.filter((l) => !l.isBase),
        ];

        updateZIndex(updatedLayers);
        return updatedLayers;
      });

      // Add the new base layer to the map
      map.addLayer(layer);
    },
    [map, removeLayer]
  );

  const rearrangeLayer = useCallback(
    (layerItem: LayerItem, newIndex: number) => {
      setLayers((prevLayers) => {
        // Remove the item from its current position
        const updatedLayers = prevLayers.filter(
          (item) => item.id !== layerItem.id
        );
        // Insert the item at the new position
        updatedLayers.splice(newIndex, 0, layerItem);

        // Update Z-Index
        updateZIndex(updatedLayers);

        // Return the updated array of layers
        return updatedLayers;
      });
    },
    [updateZIndex]
  );

  const zoomToLayer = useCallback(
    (layer: VectorLayer<Feature<OlGeometry>>) => {
      const source = layer.getSource();

      if (!(source instanceof VectorSource)) {
        return;
      }

      // Use the helper function to calculate extent with antimeridian support, see case
      //  CLASSIC-8299 from MXAK
      const extent = calculateExtentWithAntimeridianSupport(
        source,
        displayProjection,
        dataProjection
      );

      if (!extent) {
        return;
      }

      const isExtentValid = extent.every(
        (value) => value !== null && isFinite(value)
      );
      if (!isExtentValid) {
        return;
      }

      // Apply a buffer if there's only a single feature (like a point), to avoid excessive zooming
      if (source.getFeatures().length === 1) {
        const extentBuffer = 100;
        map.getView().fit(extent, {
          padding: [extentBuffer, extentBuffer, extentBuffer, extentBuffer],
          maxZoom: 16,
        });
      } else {
        map.getView().fit(extent, {});
      }
    },
    [map, displayProjection, dataProjection]
  );

  const zoomToExtent = useCallback(
    (extent: number[]) => {
      if (!extent || !Array.isArray(extent) || extent.length !== 4) {
        console.warn('Provided extent is invalid:', extent);
        return;
      }

      map.getView().fit(extent);
    },
    [map]
  );

  const getLayerById = useCallback(
    (id: string): LayerItem | undefined => {
      return layers.find((layerItem) => layerItem.id === id);
    },
    [layers]
  );

  const getLayersByIdContaining = useCallback(
    (idSubstring: string): LayerItem[] => {
      return layers.filter(
        (layerItem) => layerItem.id && layerItem.id.includes(idSubstring)
      );
    },
    [layers]
  );

  const getBaseLayer = useCallback(() => {
    return layers.find((layerItem) => layerItem.isBase);
  }, [layers]);

  const doesLayerExist = (id: string): boolean => {
    return !!getLayerById(id);
  };

  const removeLayerById = useCallback(
    (id: string | undefined) => {
      if (id) {
        const existingLayer = getLayerById(id);
        if (existingLayer) {
          removeLayer(existingLayer.layer);
        }
      }
    },
    [getLayerById, removeLayer]
  );

  const toggleLayerVisibility = useCallback(
    (layer: BaseLayer, isVisible: boolean) => {
      layer.setVisible(isVisible);
      setLayers((prevLayers) =>
        prevLayers.map((l) => (l.layer === layer ? { ...l, isVisible } : l))
      );
    },
    []
  );

  const removeLayerOnThresholdExceeded = useCallback(
    (
      layer: LayerItem,
      onLoadFail: ((layer: LayerItem) => void) | undefined
    ) => {
      if (isLayerThresholdExceeded(layer)) {
        removeLayer(layer.layer);
        if (onLoadFail) {
          onLoadFail(layer);
        }
      }
    },
    [removeLayer]
  );

  const isShipLayerVisible = useMemo(() => {
    const shipLayer = layers.find((layer) => layer.name === 'Vessels') || null;

    if (!shipLayer) {
      return false;
    }

    return shipLayer.isVisible;
  }, [layers]);

  useMapLayerHandler(
    map,
    isDrawing,
    canvasRef,
    vectorTileLayers,
    onVectorTileHover,
    dataProjection
  );

  const value = {
    layers,
    addLayer,
    addLayerAtIndex,
    addExistingLayerItem,
    addBaseLayer,
    removeLayer,
    rearrangeLayer,
    getLayerById,
    doesLayerExist,
    zoomToLayer,
    zoomToExtent,
    toggleLayerVisibility,
    removeLayerById,
    getBaseLayer,
    removeLayerOnThresholdExceeded,
    toggleLabels,
    addVectorTile,
    vectorTileHoverState,
    removeLabels,
    addLabels,
    canvasRef,
    isCanvasLoaded,
    setIsCanvasLoaded,
    changeColor,
    changeTransparency,
    getLayersByIdContaining,
    isShipLayerVisible,
  };

  return (
    <LayerContext.Provider value={value}>{children}</LayerContext.Provider>
  );
};

export const useLayers = (): LayerContextProps => {
  const context = useContext(LayerContext);
  if (!context) {
    throw new Error('useLayers must be used within a LayerProvider');
  }
  return context;
};
